/**
 * Markup Block Processor
 * Feature: 058-markdown-markup-blocks
 *
 * Utility library for parsing ```markup code blocks from markdown,
 * validating JSON shape definitions, and removing blocks from text.
 */

import type {
  MarkupShape,
  ShapeType,
  CircleGeometry,
  RectangleGeometry,
  ArrowGeometry,
  SpotlightGeometry,
} from '@/types/markup.types';
import { MARKUP_CONSTRAINTS } from '@/types/markup.types';
import logger from '@/utils/logger';

/**
 * Result of parsing markdown for markup blocks
 */
export interface MarkupBlockParseResult {
  /** Validated markup shapes extracted from blocks */
  shapes: MarkupShape[];

  /** Original markdown with ```markup blocks removed */
  cleanedMarkdown: string;

  /** Validation errors (does not prevent submission) */
  errors: string[];
}

/**
 * Internal structure for detected markup blocks
 */
interface MarkupBlock {
  content: string;
  startLine: number;
  endLine: number;
}

/**
 * Custom error for shape validation failures
 */
export class MarkupShapeValidationError extends Error {
  constructor(public field: string, message: string, public value?: unknown) {
    super(`Invalid ${field}: ${message}`);
    this.name = 'MarkupShapeValidationError';
  }
}

/**
 * Parse markdown for ```markup code blocks and extract shapes
 *
 * This function is the main entry point for markup block processing. It can be called
 * from:
 * - Comment submission hooks (useSubmitCritique) for manual user input
 * - AI systems generating critique comments with markup shapes (future)
 * - External integrations requiring programmatic markup creation
 *
 * @param markdown - Raw markdown text from comment input (can include multiple ```markup blocks)
 * @returns Parsed shapes, cleaned markdown, and any validation errors
 *
 * @example Basic usage
 * const result = parseMarkupBlocks(`
 *   Check this out:
 *
 *   \`\`\`markup
 *   { "shapes": [{ "type": "rectangle", ... }] }
 *   \`\`\`
 *
 *   What do you think?
 * `);
 *
 * // result.shapes: [{ type: "rectangle", id: "<uuid>", ... }]
 * // result.cleanedMarkdown: "Check this out:\n\nWhat do you think?"
 * // result.errors: []
 *
 * @example AI integration (future)
 * // AI generates critique comment with markup
 * const aiComment = `
 *   Based on compositional analysis:
 *   \`\`\`markup
 *   { "shapes": [
 *     { "type": "circle", "number": 1, ... },
 *     { "type": "arrow", "number": 2, ... }
 *   ]}
 *   \`\`\`
 *   See @1 for subject placement and @2 for leading lines.
 * `;
 *
 * const result = parseMarkupBlocks(aiComment);
 * // shapes extracted, markup block removed, ready for API submission
 *
 * @example Error handling
 * const result = parseMarkupBlocks(`\`\`\`markup\n{ invalid json }\n\`\`\``);
 * // result.shapes: []
 * // result.errors: ["Block 1: Invalid JSON - ..."]
 * // result.cleanedMarkdown: "" (block removed even if invalid)
 */
export function parseMarkupBlocks(markdown: string): MarkupBlockParseResult {
  logger.debug('MarkupProcessor', 'Parsing markdown for markup blocks', { markdownLength: markdown.length });

  const blocks: MarkupBlock[] = [];
  const allShapes: MarkupShape[] = [];
  const errors: string[] = [];

  // Detect ```markup blocks using line-by-line parsing
  const lines = markdown.split('\n');
  let inBlock = false;
  let blockStartLine = -1;
  let blockContent: string[] = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!inBlock && trimmed === '```markup') {
      // Start of markup block
      inBlock = true;
      blockStartLine = index;
      blockContent = [];
    } else if (inBlock && trimmed === '```') {
      // End of markup block
      inBlock = false;
      blocks.push({
        content: blockContent.join('\n'),
        startLine: blockStartLine,
        endLine: index,
      });
      blockContent = [];
    } else if (inBlock) {
      // Inside block - collect content
      blockContent.push(line);
    }
  });

  // Check for unclosed block
  if (inBlock) {
    errors.push(`Unclosed markup block starting at line ${blockStartLine + 1}`);
  }

  logger.info('MarkupProcessor', `Found ${blocks.length} markup block(s)`);

  // Parse and validate each block
  blocks.forEach((block, blockIndex) => {
    try {
      const parsed = JSON.parse(block.content);

      if (!parsed || typeof parsed !== 'object') {
        errors.push(`Block ${blockIndex + 1}: Expected JSON object`);
        return;
      }

      if (!Array.isArray(parsed.shapes)) {
        errors.push(`Block ${blockIndex + 1}: Missing "shapes" array`);
        return;
      }

      const { valid, errors: validationErrors } = validateMarkupShapes(parsed.shapes);
      allShapes.push(...valid);
      errors.push(...validationErrors.map((err) => `Block ${blockIndex + 1}: ${err}`));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      errors.push(`Block ${blockIndex + 1}: Invalid JSON - ${message}`);
      logger.warn('MarkupProcessor', `Failed to parse block ${blockIndex + 1}`, { error });
    }
  });

  // Enforce max shapes constraint
  if (allShapes.length > MARKUP_CONSTRAINTS.MAX_SHAPES) {
    errors.push(`Too many shapes: ${allShapes.length} (max ${MARKUP_CONSTRAINTS.MAX_SHAPES})`);
    allShapes.splice(MARKUP_CONSTRAINTS.MAX_SHAPES); // Keep only first 9
  }

  // Remove blocks from markdown
  const cleanedMarkdown = removeMarkupBlocks(
    markdown,
    blocks.map((b) => ({ start: b.startLine, end: b.endLine }))
  );

  logger.info('MarkupProcessor', `Parsed ${allShapes.length} valid shape(s)`, { errorCount: errors.length });

  return {
    shapes: allShapes,
    cleanedMarkdown,
    errors,
  };
}

/**
 * Validate markup shape JSON against schema
 *
 * @param data - Unknown data from JSON.parse()
 * @returns Valid shapes and error messages
 *
 * @throws Never throws - collects all errors instead
 *
 * @example
 * const json = JSON.parse(blockContent);
 * const { valid, errors } = validateMarkupShapes(json.shapes);
 *
 * if (errors.length > 0) {
 *   console.warn('Invalid shapes:', errors);
 * }
 */
export function validateMarkupShapes(data: unknown): {
  valid: MarkupShape[];
  errors: string[];
} {
  if (!Array.isArray(data)) {
    return { valid: [], errors: ['Expected an array of shapes'] };
  }

  const valid: MarkupShape[] = [];
  const errors: string[] = [];

  data.forEach((item, index) => {
    try {
      const validated = validateMarkupShape(item);
      valid.push(validated);
    } catch (error) {
      if (error instanceof MarkupShapeValidationError) {
        errors.push(`Shape ${index + 1}: ${error.message}`);
      } else {
        const message = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Shape ${index + 1}: ${message}`);
      }
    }
  });

  return { valid, errors };
}

/**
 * Validate a single markup shape
 *
 * @param data - Unknown shape object
 * @returns Validated MarkupShape with regenerated UUID
 * @throws MarkupShapeValidationError if invalid
 */
export function validateMarkupShape(data: unknown): MarkupShape {
  if (!data || typeof data !== 'object') {
    throw new MarkupShapeValidationError('shape', 'Must be an object', data);
  }

  const shape = data as Record<string, unknown>;

  // Validate type field
  if (typeof shape.type !== 'string') {
    throw new MarkupShapeValidationError('type', 'Must be a string', shape.type);
  }

  const validTypes: ShapeType[] = ['rectangle', 'circle', 'arrow', 'spotlight'];
  if (!validTypes.includes(shape.type as ShapeType)) {
    throw new MarkupShapeValidationError('type', `Must be one of: ${validTypes.join(', ')}`, shape.type);
  }

  // Validate number field
  if (typeof shape.number !== 'number') {
    throw new MarkupShapeValidationError('number', 'Must be a number', shape.number);
  }
  if (shape.number < MARKUP_CONSTRAINTS.MIN_NUMBER || shape.number > MARKUP_CONSTRAINTS.MAX_NUMBER) {
    throw new MarkupShapeValidationError(
      'number',
      `Must be between ${MARKUP_CONSTRAINTS.MIN_NUMBER} and ${MARKUP_CONSTRAINTS.MAX_NUMBER}`,
      shape.number
    );
  }

  // Validate style fields
  if (typeof shape.strokeWidth !== 'number') {
    throw new MarkupShapeValidationError('strokeWidth', 'Must be a number', shape.strokeWidth);
  }
  if (shape.strokeWidth < 3 || shape.strokeWidth > 10) {
    throw new MarkupShapeValidationError('strokeWidth', 'Must be between 3 and 10', shape.strokeWidth);
  }

  if (typeof shape.strokeColor !== 'string') {
    throw new MarkupShapeValidationError('strokeColor', 'Must be a string', shape.strokeColor);
  }
  if (!/^#[0-9A-Fa-f]{8}$/.test(shape.strokeColor)) {
    throw new MarkupShapeValidationError('strokeColor', 'Must be RGBA hex format (#RRGGBBAA)', shape.strokeColor);
  }

  if (typeof shape.strokeDasharray !== 'string') {
    throw new MarkupShapeValidationError('strokeDasharray', 'Must be a string', shape.strokeDasharray);
  }
  const validDashArrays = ['', '5,5', '2,2', '__hand_drawn__'];
  if (!validDashArrays.includes(shape.strokeDasharray)) {
    throw new MarkupShapeValidationError(
      'strokeDasharray',
      `Must be one of: ${validDashArrays.map((v) => `"${v}"`).join(', ')}`,
      shape.strokeDasharray
    );
  }

  // Validate geometry based on type
  if (!shape.geometry || typeof shape.geometry !== 'object') {
    throw new MarkupShapeValidationError('geometry', 'Must be an object', shape.geometry);
  }

  const shapeType = shape.type as ShapeType;
  let validatedGeometry: CircleGeometry | RectangleGeometry | ArrowGeometry | SpotlightGeometry;

  switch (shapeType) {
    case 'circle':
      validatedGeometry = validateCircleGeometry(shape.geometry);
      break;
    case 'rectangle':
      validatedGeometry = validateRectangleGeometry(shape.geometry);
      break;
    case 'arrow':
      validatedGeometry = validateArrowGeometry(shape.geometry);
      break;
    case 'spotlight':
      validatedGeometry = validateSpotlightGeometry(shape.geometry);
      break;
  }

  // Build validated shape without ID (IDs are not stored in database)
  const validatedShape: MarkupShape = {
    type: shapeType,
    number: shape.number as number,
    strokeWidth: shape.strokeWidth as number,
    strokeColor: shape.strokeColor as string,
    strokeDasharray: shape.strokeDasharray as string,
    geometry: validatedGeometry,
  } as MarkupShape;

  return validatedShape;
}

/**
 * Validate circle geometry
 */
function validateCircleGeometry(geometry: unknown): CircleGeometry {
  const geo = geometry as Record<string, unknown>;

  validatePercentageField(geo, 'cx', 0, 100);
  validatePercentageField(geo, 'cy', 0, 100);
  validatePercentageField(geo, 'r', MARKUP_CONSTRAINTS.MIN_CIRCLE_RADIUS, 50);

  return {
    cx: geo.cx as number,
    cy: geo.cy as number,
    r: geo.r as number,
  };
}

/**
 * Validate rectangle geometry
 */
function validateRectangleGeometry(geometry: unknown): RectangleGeometry {
  const geo = geometry as Record<string, unknown>;

  validatePercentageField(geo, 'x', 0, 100);
  validatePercentageField(geo, 'y', 0, 100);
  validatePercentageField(geo, 'width', MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE, 100);
  validatePercentageField(geo, 'height', MARKUP_CONSTRAINTS.MIN_RECTANGLE_SIZE, 100);

  // Validate bounds
  const x = geo.x as number;
  const y = geo.y as number;
  const width = geo.width as number;
  const height = geo.height as number;

  if (x + width > 100) {
    throw new MarkupShapeValidationError('geometry.width', 'Rectangle exceeds right boundary', { x, width });
  }
  if (y + height > 100) {
    throw new MarkupShapeValidationError('geometry.height', 'Rectangle exceeds bottom boundary', { y, height });
  }

  return { x, y, width, height };
}

/**
 * Validate arrow geometry
 */
function validateArrowGeometry(geometry: unknown): ArrowGeometry {
  const geo = geometry as Record<string, unknown>;

  validatePercentageField(geo, 'x1', 0, 100);
  validatePercentageField(geo, 'y1', 0, 100);
  validatePercentageField(geo, 'x2', 0, 100);
  validatePercentageField(geo, 'y2', 0, 100);

  // Validate minimum length
  const x1 = geo.x1 as number;
  const y1 = geo.y1 as number;
  const x2 = geo.x2 as number;
  const y2 = geo.y2 as number;

  const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  if (distance < MARKUP_CONSTRAINTS.MIN_ARROW_LENGTH) {
    throw new MarkupShapeValidationError(
      'geometry',
      `Arrow length must be at least ${MARKUP_CONSTRAINTS.MIN_ARROW_LENGTH}%`,
      { distance }
    );
  }

  return { x1, y1, x2, y2 };
}

/**
 * Validate spotlight geometry (same as rectangle)
 */
function validateSpotlightGeometry(geometry: unknown): SpotlightGeometry {
  return validateRectangleGeometry(geometry);
}

/**
 * Validate a percentage-based numeric field
 */
function validatePercentageField(
  obj: Record<string, unknown>,
  field: string,
  min: number,
  max: number
): void {
  const value = obj[field];

  if (typeof value !== 'number') {
    throw new MarkupShapeValidationError(`geometry.${field}`, 'Must be a number', value);
  }

  if (value < min || value > max) {
    throw new MarkupShapeValidationError(
      `geometry.${field}`,
      `Must be between ${min} and ${max}`,
      value
    );
  }
}

/**
 * Internal: Remove markup blocks from markdown using line ranges
 * Not exported - implementation detail of parseMarkupBlocks()
 */
function removeMarkupBlocks(
  markdown: string,
  blockRanges: Array<{ start: number; end: number }>
): string {
  if (blockRanges.length === 0) {
    return markdown;
  }

  const lines = markdown.split('\n');
  const filteredLines = lines.filter((_line, index) => {
    return !blockRanges.some((range) => index >= range.start && index <= range.end);
  });

  // Join and then clean up multiple consecutive blank lines
  let result = filteredLines.join('\n');

  // Replace multiple consecutive newlines with just two newlines (preserves paragraph breaks)
  result = result.replace(/\n{3,}/g, '\n\n');

  return result.trim();
}
